/**
 * Flat progress bar for a bounded count (usage vs. tier limit) — follows the
 * design system's `rounded-none` convention (Card.tsx's docstring: pill
 * shape is reserved for Badge chips only). Renders only when the caller has
 * a real, finite `max`; a `null`/unlimited tier (TTP-245's current reality —
 * see `UsagePage.tsx`) is a separate "sem limite definido" text state, never
 * an empty or full bar here.
 */
export function ProgressBar({ value, max, label }: { value: number; max: number; label: string }) {
  const safeMax = max > 0 ? max : 1;
  const ratio = Math.min(1, Math.max(0, value / safeMax));
  const percent = Math.round(ratio * 100);
  const overLimit = value > max;

  return (
    <div className="flex flex-col gap-1.5">
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
        className="h-2 w-full overflow-hidden rounded-none border border-border bg-muted/40"
      >
        <div
          className={`h-full ${overLimit ? 'bg-accent' : 'bg-primary'}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="font-mono text-xs text-muted-foreground">
        {value.toLocaleString('pt-BR')} / {max.toLocaleString('pt-BR')} ({percent}%)
      </p>
    </div>
  );
}
